import { cn } from "cn";
import React from "react";

/**
 * The house EASE_OUT_STRONG curve (webapp/src/components/animations/constants.ts)
 * expressed for CSS: strong ease-out, so the indicator leaves quickly and settles.
 */
const INDICATOR_TRANSITION =
  "transition-[transform,width] duration-[280ms] ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none";

/**
 * Slides one indicator element to sit behind whichever trigger is active.
 *
 * Two deliberate choices, both about staying smooth while the surrounding screen
 * re-renders:
 *
 * 1. The active trigger is read from Radix's own `data-state` in the DOM rather
 *    than mirrored into React state, so there is no second source of truth to
 *    fall out of sync on a controlled/uncontrolled or async value update.
 * 2. Position and size are written imperatively and animated by a CSS
 *    transition, which the browser's animation timeline owns. A React render
 *    cannot reset, restart or starve it. A JS-driven layout animation
 *    (Framer Motion `layoutId`) was measured doing exactly that: under a
 *    re-rendering parent its time-based spring skipped ~220px in a single frame
 *    instead of animating.
 *
 * `transform` is composited, so the travel stays smooth even when the main
 * thread is busy; `width` still costs layout on this one small absolutely
 * positioned element, which is the accepted trade for an undistorted border and
 * border-radius (scaleX would squash both).
 */
export function useTabsIndicator<T extends HTMLElement>() {
  const listRef = React.useRef<T>(null);
  const indicatorRef = React.useRef<HTMLSpanElement>(null);

  React.useLayoutEffect(() => {
    const list = listRef.current;
    const indicator = indicatorRef.current;
    if (!list || !indicator) return;

    // The first placement jumps into position; only later moves animate.
    let placed = false;

    const sync = () => {
      const active = list.querySelector<HTMLElement>(
        '[role="tab"][data-state="active"]',
      );
      if (!active) {
        indicator.style.opacity = "0";
        placed = false;
        return;
      }
      // Rects, not offsetLeft/offsetWidth: those round to whole pixels, which
      // leaves the indicator up to half a pixel off a fractionally-sized trigger.
      // scroll offsets put the result back in the list's content coordinates
      // (the indicator scrolls with the content), clientLeft/Top discount the
      // list's border, since an absolute child is placed against the padding box.
      const listBox = list.getBoundingClientRect();
      const activeBox = active.getBoundingClientRect();
      // Rects are in VISUAL pixels — an ancestor's scale is already baked into
      // them — but the values written back below are LAYOUT pixels, which that
      // same ancestor scales again. Undo the scale, or a tab list mounted while
      // a dialog is still playing `zoom-in-95` keeps geometry ~5% short forever:
      // invisible on the first trigger, where the offset is ~0, but the second
      // one lands over a dozen pixels left of its label. offsetWidth is the
      // untransformed layout width, so their ratio is the accumulated scale
      // (1 when nothing above is transformed, and when the list has no layout).
      const scale = list.offsetWidth > 0 ? listBox.width / list.offsetWidth : 1;
      const toLayout = (visual: number) =>
        scale > 0 ? visual / scale : visual;
      const x =
        toLayout(activeBox.left - listBox.left) -
        list.clientLeft +
        list.scrollLeft;
      const y =
        toLayout(activeBox.top - listBox.top) - list.clientTop + list.scrollTop;

      if (!placed) indicator.style.transition = "none";
      indicator.style.opacity = "1";
      indicator.style.width = `${toLayout(activeBox.width)}px`;
      indicator.style.height = `${toLayout(activeBox.height)}px`;
      indicator.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      if (!placed) {
        indicator.getBoundingClientRect(); // flush, then hand it back to CSS
        indicator.style.transition = "";
        placed = true;
      }
    };

    const resizeObserver = new ResizeObserver(sync);
    // Re-bind rather than observe inside `sync`: re-observing an already-observed
    // element fires the callback again, which would loop.
    const bindResize = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(list);
      for (const trigger of list.querySelectorAll('[role="tab"]')) {
        resizeObserver.observe(trigger);
      }
    };

    const mutationObserver = new MutationObserver((records) => {
      if (records.some((record) => record.type === "childList")) bindResize();
      sync();
    });
    mutationObserver.observe(list, {
      attributes: true,
      attributeFilter: ["data-state"],
      childList: true,
      subtree: true,
    });

    sync();
    bindResize();

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  return { listRef, indicatorRef };
}

/**
 * The sliding element itself. Sized and positioned by `useTabsIndicator`; the
 * caller supplies only its looks. Renders before the triggers so it paints
 * behind their labels.
 */
export const TabsIndicator = ({
  ref,
  className,
}: {
  ref: React.Ref<HTMLSpanElement>;
  className?: string;
}) => {
  return (
    <span
      ref={ref}
      aria-hidden
      data-slot="tabs-indicator"
      className={cn(
        /* sorted */
        "absolute top-0 left-0",
        "opacity-0 pointer-events-none",
        INDICATOR_TRANSITION,
        className,
      )}
    />
  );
};
